import React, { Suspense } from 'react';

// Posé à la compilation (voir vite.config.js) : commit + date de la mise en ligne.
const VERSION = typeof __BUILD__ !== 'undefined' ? __BUILD__ : 'dev';

/**
 * Garde-fous contre la page blanche.
 *
 * Deux causes distinctes produisaient le même écran blanc :
 *
 * 1. Une erreur pendant l'affichage d'un module. Sans garde-fou, React
 *    démonte TOUTE l'application : il ne reste plus rien à l'écran et il faut
 *    quitter l'app. Seul « Préparation export » était protégé.
 *
 * 2. Une nouvelle version déployée pendant que l'app est ouverte. Les fichiers
 *    de l'ancienne version n'existent plus sur le serveur ; au moment d'ouvrir
 *    un module, son fichier est introuvable (404) et l'app meurt. C'est le cas
 *    le plus fréquent : ça arrive à chaque mise en ligne, sur n'importe quel
 *    module, et ça n'a rien à voir avec le module lui-même.
 *
 * Ici : une erreur affiche un message lisible avec un bouton Recharger, et une
 * version périmée recharge la page toute seule (une seule fois, pour ne jamais
 * boucler).
 */

export function estErreurDeVersion(err) {
  const m = String(err?.message || err || '');
  // « is not a valid JavaScript MIME type » : le serveur a renvoyé la page
  // d'accueil à la place d'un fichier de l'ancienne version. Même cause,
  // même remède — c'est ce message que Safari affichait après chaque mise
  // en ligne, et qui échappait au rechargement automatique.
  return /dynamically imported module|Importing a module script failed|Loading chunk|ChunkLoadError|Failed to fetch dynamically|not a valid JavaScript MIME type|Unexpected token '<'/i.test(m);
}

const CLE_RECHARGE = 'elixir_reload_version';

// Recharge au plus une fois par minute : si le problème persiste après le
// rechargement, on affiche le message au lieu de boucler indéfiniment.
export function rechargerPourNouvelleVersion() {
  try {
    const dernier = Number(sessionStorage.getItem(CLE_RECHARGE) || 0);
    if (Date.now() - dernier < 60000) return false;
    sessionStorage.setItem(CLE_RECHARGE, String(Date.now()));
  } catch { /* mode privé : on recharge quand même */ }
  window.location.reload();
  return true;
}

// Nom lisible d'un module à partir de son identifiant. L'écran d'erreur
// doit dire OÙ ça a cassé : « une erreur est survenue » sans le module
// oblige à deviner, et on cherche au mauvais endroit.
const NOMS_MODULES = {
  prep: 'Préparation pharmacies', reception_t: 'Réception transporteurs',
  reception_p: 'Réception produits', reception_v2: 'Réception V2', prep_v2: 'Prépa V2',
  reclam: 'Réclamations pharmacies', tickets: 'Tickets service client', stock_cas: 'Gestion de stock', impression: 'Impression des fiches qualité',
  derogations: 'Dérogations hors secteur', livraison_samedi: 'Livraisons du samedi',
  export: 'Préparation export', litiges: 'Litiges clients export',
  erreurs: 'Suivi des erreurs', stups: 'Stupéfiants', corrections: 'Corrections réception',
  controle_prep: 'Contrôle préparation', stats: 'Statistiques',
  historique: 'Historique & suivi', equipe: 'Équipe & permissions',
};

export class Boundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null, version: false }; }

  static getDerivedStateFromError(err) {
    return { err, version: estErreurDeVersion(err) };
  }

  componentDidCatch(err, info) {
    console.error('Erreur attrapée (' + (this.props.nom || this.props.cle || 'app') + ') :', err, info);
    this.setState({ pile: String(info?.componentStack || '').trim().split('\n').slice(0, 4).join('\n') });
    if (estErreurDeVersion(err)) rechargerPourNouvelleVersion();
  }

  // Changer de module efface l'erreur précédente
  componentDidUpdate(prev) {
    if (prev.cle !== this.props.cle && this.state.err) this.setState({ err: null, version: false, pile: null });
  }

  render() {
    if (!this.state.err) return this.props.children;
    const nomLisible = this.props.nom || NOMS_MODULES[this.props.cle] || null;

    if (this.state.version) return (
      <div style={{ padding: 16 }}>
        <div style={{ padding: 14, borderRadius: 10, background: '#EAF1F7', border: '1.5px solid #AFCBE3', color: '#1A5276', fontSize: 13 }}>
          <b>Une nouvelle version de l’app est en ligne.</b>
          <div style={{ marginTop: 4 }}>Recharge pour continuer — rien n’est perdu.</div>
          <button onClick={() => window.location.reload()} style={{ marginTop: 10, padding: '10px 14px', borderRadius: 9, border: 'none', background: '#1A5276', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>↻ Recharger</button>
        </div>
      </div>
    );

    return (
      <div style={{ padding: 16 }}>
        <div style={{ padding: 14, borderRadius: 10, background: '#FDECEA', border: '1.5px solid #C0392B', color: '#C0392B', fontSize: 13 }}>
          <b>{nomLisible ? 'Le module « ' + nomLisible + ' » a rencontré une erreur.' : 'Une erreur est survenue.'}</b>
          <div style={{ marginTop: 4, wordBreak: 'break-word' }}>{String(this.state.err?.message || this.state.err)}</div>
          {this.state.pile && (
            <pre style={{ marginTop: 6, fontSize: 10, color: '#7F2A20', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, monospace' }}>{this.state.pile}</pre>
          )}
          <div style={{ color: '#6B7280', marginTop: 6 }}>Le reste de l’app fonctionne : tu peux revenir en arrière. Si ça revient, envoie cette capture à Zineb.</div>
          <div style={{ color: '#9CA3AF', marginTop: 4, fontSize: 10, fontFamily: 'ui-monospace, monospace' }}>version {VERSION}</div>
          <button onClick={() => window.location.reload()} style={{ marginTop: 10, padding: '10px 14px', borderRadius: 9, border: 'none', background: '#C0392B', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>↻ Recharger</button>
        </div>
      </div>
    );
  }
}

const Attente = <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 13 }}>Chargement…</div>;

/** Module chargé à la demande : protégé ET avec son écran d'attente. */
export function Lazy({ cle, nom, children }) {
  return (
    <Boundary cle={cle} nom={nom}>
      <Suspense fallback={Attente}>{children}</Suspense>
    </Boundary>
  );
}

/**
 * Un fichier de module qui n'arrive pas à se charger déclenche un rejet de
 * promesse hors rendu : React ne le voit pas. On l'attrape globalement.
 */
export function installerGardeFouVersion() {
  const check = (e) => {
    const err = e?.reason || e?.error || e;
    if (estErreurDeVersion(err)) rechargerPourNouvelleVersion();
  };
  window.addEventListener('unhandledrejection', check);
  window.addEventListener('error', check);
}
